<template>
  <div class="main__content content parking-complex-page">
    <ParkingEmptyState
      v-if="!isParkingDetectionAvailable"
      title="Аналитика парковочных мест недоступна"
      description="Подключите аналитику парковочных мест, чтобы создавать парковочные комплексы"
    />

    <ParkingComplexEditor
      v-else-if="isCreatingComplex"
      :parking-complex="selectedComplex"
      :initial-expanded-parking-id="initialExpandedParkingId"
      @back="closeComplexEditor"
      @saved="handleComplexSaved"
    />

    <div
      v-else-if="isLoading"
      class="parking-complex-page__loader"
      role="status"
      aria-label="Загрузка парковочных комплексов"
    >
      <SpinnerLoading color="blue" />
    </div>

    <ParkingComplexList
      v-else
      :complexes="parkingComplexes"
      :expanded-complex-id="expandedComplexId"
      :selected-parking-id="selectedParkingId"
      :parking-cameras="parkingCameras"
      :parking-cameras-loading="isLoadingParkingCameras"
      @create="openComplexEditor()"
      @edit="openComplexEditor"
      @edit-parking="openParkingEditor"
      @delete="deleteComplex"
      @delete-parking="deleteParking"
      @toggle-complex="toggleComplex"
      @select-parking="selectParking"
      @camera-updated="reloadSelectedParkingCameras"
    />
  </div>
</template>

<script>
import ParkingComplexEditor from "@/components/pacs/analytics/parkingDetection/ParkingComplexEditor.vue";
import ParkingComplexList from "@/components/pacs/analytics/parkingDetection/ParkingComplexList.vue";
import ParkingEmptyState from "@/components/pacs/analytics/parkingDetection/ParkingEmptyState.vue";
import {PARKING_AREAS_REQUEST} from "@/components/pacs/analytics/parkingDetection/parkingComplexConfig.js";
import {ANALYTICS} from "@/store/analytics/helpers.js";
import {
  ACTION_DELETE_PARKINGS,
  ACTION_DELETE_PARKING_AREAS,
  ACTION_LOAD_PARKING_AREAS,
  ACTION_LOAD_PARKING_CAMERAS,
} from "@/store/analytics/parkingDetections/index.js";

const PARKING_STORE_NAMESPACE = "analytics/parkingDetections";

const mapParkingAreasResponse = (response = {}) => {
  const parkingById = new Map(
    ((response.extra && response.extra.parking) || []).map(parking => [parking.id, parking])
  );

  return (response.results || []).map(parkingArea => ({
    ...parkingArea,
    parkings: (parkingArea.parking_ids || [])
      .map(parkingId => parkingById.get(parkingId))
      .filter(parking => parking && !parking.is_deleted),
  }));
};

/**
 * Контейнер раздела парковочных комплексов.
 * Хранит раскрытый комплекс и выбранную парковку, а таблице передает только ее камеры.
 */
export default {
  name: "ParkingComplexesPage",
  components: {
    ParkingComplexEditor,
    ParkingComplexList,
    ParkingEmptyState,
  },
  props: {
    /** Список аналитик, уже загруженный родителем через ACTION_LOAD_AVAILABLE_ANALYTICS. */
    availableAnalytics: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      expandedComplexId: null,
      initialExpandedParkingId: null,
      isCreatingComplex: false,
      isLoading: false,
      isLoadingParkingCameras: false,
      parkingCameras: [],
      parkingCamerasRequestId: 0,
      parkingComplexes: [],
      selectedComplex: null,
      selectedParkingId: null,
    };
  },
  computed: {
    isParkingDetectionAvailable() {
      return this.availableAnalytics.includes(ANALYTICS.parking_detection);
    },
  },
  watch: {
    isParkingDetectionAvailable: {
      immediate: true,
      handler(isAvailable) {
        if (isAvailable) this.loadComplexes();
      },
    },
  },
  methods: {
    async loadComplexes() {
      this.isLoading = true;
      try {
        const response = await this.$store.dispatch(
          `${PARKING_STORE_NAMESPACE}/${ACTION_LOAD_PARKING_AREAS}`,
          PARKING_AREAS_REQUEST
        );
        this.parkingComplexes = mapParkingAreasResponse(response);
        this.reconcileSelection();
      } catch (error) {
        this.notify(error.message || "Не удалось загрузить парковочные комплексы", "error");
      } finally {
        this.isLoading = false;
      }
    },
    reconcileSelection() {
      const expandedComplex = this.parkingComplexes.find(
        parkingComplex => parkingComplex.id === this.expandedComplexId
      );

      if (!expandedComplex) {
        this.clearHierarchySelection();
        return;
      }

      const hasSelectedParking = (expandedComplex.parkings || []).some(
        parking => parking.id === this.selectedParkingId
      );
      if (!hasSelectedParking) this.clearParkingSelection();
    },
    toggleComplex(parkingComplex) {
      if (this.expandedComplexId === parkingComplex.id) {
        this.clearHierarchySelection();
        return;
      }

      this.expandedComplexId = parkingComplex.id;
      this.clearParkingSelection();
    },
    async selectParking(parking) {
      this.selectedParkingId = parking.id;
      this.parkingCameras = [];
      this.isLoadingParkingCameras = true;
      const requestId = ++this.parkingCamerasRequestId;

      try {
        const cameras = await this.$store.dispatch(
          `${PARKING_STORE_NAMESPACE}/${ACTION_LOAD_PARKING_CAMERAS}`,
          {
            cameraNumbers: parking.camera_numbers,
          }
        );
        if (requestId === this.parkingCamerasRequestId) {
          this.parkingCameras = cameras;
        }
      } catch (error) {
        if (requestId === this.parkingCamerasRequestId) {
          this.notify(error.message || "Не удалось загрузить камеры парковки", "error");
        }
      } finally {
        if (requestId === this.parkingCamerasRequestId) {
          this.isLoadingParkingCameras = false;
        }
      }
    },
    reloadSelectedParkingCameras() {
      const expandedComplex = this.parkingComplexes.find(
        parkingComplex => parkingComplex.id === this.expandedComplexId
      );
      const selectedParking = expandedComplex
        && (expandedComplex.parkings || []).find(
          parking => parking.id === this.selectedParkingId
        );

      if (selectedParking) this.selectParking(selectedParking);
    },
    clearParkingSelection() {
      this.parkingCamerasRequestId += 1;
      this.selectedParkingId = null;
      this.parkingCameras = [];
      this.isLoadingParkingCameras = false;
    },
    clearHierarchySelection() {
      this.expandedComplexId = null;
      this.clearParkingSelection();
    },
    openComplexEditor(parkingComplex = null) {
      this.initialExpandedParkingId = null;
      this.selectedComplex = parkingComplex;
      this.isCreatingComplex = true;
    },
    openParkingEditor({parkingComplex, parking}) {
      this.initialExpandedParkingId = parking.id;
      this.selectedComplex = parkingComplex;
      this.isCreatingComplex = true;
    },
    closeComplexEditor() {
      this.initialExpandedParkingId = null;
      this.selectedComplex = null;
      this.isCreatingComplex = false;
    },
    async handleComplexSaved() {
      this.closeComplexEditor();
      await this.loadComplexes();
    },
    deleteComplex(parkingComplex) {
      this.$camsdals.confirm(
        `Удалить парковочный комплекс «${parkingComplex.name}»?`,
        async () => {
          try {
            await this.$store.dispatch(
              `${PARKING_STORE_NAMESPACE}/${ACTION_DELETE_PARKING_AREAS}`,
              {ids: [parkingComplex.id], comment: ""}
            );
            await this.loadComplexes();
            this.notify("Парковочный комплекс удалён", "success");
          } catch (error) {
            this.notify(error.message || "Не удалось удалить парковочный комплекс", "error");
          }
        }
      );
    },
    deleteParking({parking}) {
      this.$camsdals.confirm(
        `Удалить парковку «${parking.name}»?`,
        async () => {
          try {
            await this.$store.dispatch(
              `${PARKING_STORE_NAMESPACE}/${ACTION_DELETE_PARKINGS}`,
              {ids: [parking.id], comment: ""}
            );
            await this.loadComplexes();
            this.notify("Парковка удалена", "success");
          } catch (error) {
            this.notify(error.message || "Не удалось удалить парковку", "error");
          }
        }
      );
    },
    notify(text, type) {
      this.$notify({group: "main", text, duration: 4000, type});
    },
  },
};
</script>

<style lang="scss">
@use "./style.scss";
</style>
